import { Injectable } from '@angular/core';
import { safeUrlForLog } from 'app/safe-url-for-log';
import { timeout } from '@cf-shared/shared-util';
import { ToastType } from 'components/toast/toast-host';
import { getRootDomain } from 'environments/environment-helpers';
import { LL } from 'i18n/i18n.service';
import { LoadingController } from 'providers/loading-controller';
import { ToastController } from 'providers/toast-controller';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { handleSsoLanding } from 'services/discourse-return-host';
import { LoggingService } from 'services/logging.service';
import { SettingsService } from 'services/settings.service';
import { SsoExchangeOutcome, decideSsoExchangeOutcome } from 'services/sso-exchange-outcome';
import { UrlQueryService } from 'services/url-query-service';

@Injectable()
export class SsoService {
  constructor(private cloudFunctionsService: CloudFunctionsService, private auth: AuthService, private logging: LoggingService, private settings: SettingsService, private loading: LoadingController, private urlQuery: UrlQueryService, private toastCtrl: ToastController) { }

  sso: string;
  sig: string;

  parseUrl(): boolean {
    // the decision itself lives in the Angular-free module beside the parser, so it can
    // be unit-tested without a browser. This method only hands it the real page.
    //
    // MUST STAY SYNCHRONOUS, AND MUST STAY IN THE AppComponent CONSTRUCTOR. Clearing the
    // pair out of the address bar is what keeps it out of Mixpanel's session replay and
    // its heatmap events (#5151), and the SDK can start filming this page by ITSELF:
    // `mixpanel.init` ends by looking for a recording belonging to this browser tab and
    // resuming it, which is exactly what a community sign-in does — the person leaves for
    // the forum and comes back in the same tab. That lookup waits on an IndexedDB promise,
    // so it lands after this synchronous work. Put an `await` in front of this call, or
    // move it out of the constructor, and the credential is filmed again with nothing to
    // warn you.
    const outcome = handleSsoLanding({
      href: location.href,
      host: location.host,
      search: location.search,
      rootDomain: getRootDomain(),
      bounceTo: url => {
        // safe to print: the host is the whole point of this line and carries no credential
        console.log(`SSO: bouncing to originating host ${new URL(url).host}`);
        location.replace(url);
      },
      // keep whatever state the page already had — the nav controller keys on it
      rewriteAddress: url => history.replaceState(history.state, '', url),
    });
    if (outcome.kind !== 'taken') {
      return false;
    }

    // stored still percent-encoded; completeExchangeAsync does the one decode
    this.sso = outcome.sso;
    this.sig = outcome.sig;
    // the address no longer holds the pair, so make the boot-time query snapshot forget it
    // too — `getAllUrlParamsAsObject` ships that snapshot whole as event properties.
    this.urlQuery.forgetUrlParams(['sso', 'sig']);
    return true;
  }

  isSsoRequested(): boolean {
    return !!this.sso && !!this.sig;
  }

  // run the exchange for the pair `parseUrl` read off this page's address.
  // always returns false (the boolean is a vestigial signal — postLoginAsync uses
  // it as a "did we already navigate away?" gate but the success path's
  // location.replace ends the page first).
  async redirect(): Promise<boolean> {
    if (!this.sso || !this.sig) {
      return false;
    }
    await this.completeExchangeAsync(this.sso, this.sig);
    return false;
  }

  /**
   * Trade the pair the forum handed back for a signed reply, and send the person on
   * to the forum with it. Called by `redirect` above on the web, and directly by the
   * deep-link flow on a phone (#4688).
   *
   * The last step MUST stay a plain page navigation rather than the in-app browser
   * helper, or community sign-in breaks on iOS while still working on Android. Why,
   * and what the phone's legs of the handshake are, are in
   * design-docs/discourse-sso.md, "The return leg on a phone".
   */
  async completeExchangeAsync(sso: string, sig: string): Promise<void> {
    const loader = this.loading.create({ content: LL.sso.loggingIntoCommunity() });
    await loader.present();
    try {
      // wait for Firebase to populate currentUser before issuing ssoDiscourse.
      // warm-resume Phase A emits logonChanged → postLoginAsync → sso.redirect
      // before fbAuth.currentUser is set, so a synchronous call here would 401
      // (#3298). pattern mirrors runElasticQueryAsync in food-search-elastic.ts (#3246).
      // narrow the catch to TimeoutError so a future change that rejects
      // whenFirebaseAuthSettled isn't silently treated as a timeout.
      // unlike the food search we don't short-circuit on a still-null
      // currentUser after the wait — both callers only get here with a pair the
      // forum handed back, so a genuinely-logged-out entry is not a normal state;
      // the cloud function reports its own 401 via result.error.
      try {
        await timeout(10_000, this.auth.whenFirebaseAuthSettled);
      } catch (e) {
        if (e?.name !== 'TimeoutError') throw e;
        console.log('SSO: proceeding without Firebase auth-settled (timed out)');
      }

      console.log(`SSO: Getting SSO credentials.`)
      // the one place the pair is decoded. It arrives percent-encoded from the address
      // and this can throw on a corrupted or crafted link — which is exactly why it is
      // here, inside the catch below, rather than in the parser, where the failure
      // would reach nobody.
      const result = await this.cloudFunctionsService.ssoDiscourse({ sso: decodeURIComponent(sso), sig });

      const outcome = decideSsoExchangeOutcome(result);
      // if the person has no subscription, the forum's access rule refused them as
      // designed: say why, and count it as information rather than a failure (#6608)
      if (outcome === SsoExchangeOutcome.NotSubscribed) {
        this.logging.trackSilentInfo('CommunitySsoNotSubscribed', {});
        this.toastCtrl.showToast({
          message: LL.sso.communityForSubscribersOnly(),
          type: ToastType.WarningLight,
          duration: 5000,
        });
        return;
      }
      if (outcome === SsoExchangeOutcome.Failed) {
        this.logging.trackAbnormalError(result.error, LL.errors.userFacing.communityAuthFailed())
        return;
      }

      // do NOT log result.redirectUrl whole — its query string carries the base64 payload and the
      // HMAC that together ARE the forum login for this user, and every console call in this app
      // becomes a Sentry breadcrumb and lands in the E2E harness's published console log (#4525).
      // The host and path say where the user is being sent, which is what this line is read for.
      console.log(`SSO: Redirecting to "${safeUrlForLog(result.redirectUrl)}".`)
      this.settings.usedCommunityBefore = true;
      await this.settings.saveAsync();
      location.replace(result.redirectUrl);
    } catch (err) {
      // the call can REJECT rather than answer with result.error, and decoding the payload
      // or saving the settings can throw too. Uncaught, every one of those would travel out
      // of a promise nobody awaits on the phone path and reach no one at all.
      //
      // Reporting is not the same as telling the PERSON, and the difference is deliberate:
      // a lost connection and a retriable 502/503 return early inside trackAbnormalException
      // because the cloud-function wrapper has already reported them and this offline-first
      // app does not put a message in front of every network blip. Those reach Sentry and
      // show nothing on screen. Everything else — an unreadable payload, a 500, a failed
      // save — shows the same message a refused sign-in shows. See
      // design-docs/discourse-sso.md, "The return leg on a phone".
      this.logging.trackAbnormalException(err, LL.errors.userFacing.communityAuthFailed());
    } finally {
      await loader.dismiss();
    }
  }
}
